import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { devDebug } from "@/lib/debug";

// Global registry of pending deletes (shared across all master pages).
// Keyed by a unique symbol so we can flush all-at-once on beforeunload.
const _pending = new Map(); // key: string → { timer, apiCall, restore }

let _beforeUnloadInstalled = false;
function _ensureBeforeUnloadHook() {
  if (_beforeUnloadInstalled || typeof window === "undefined") return;
  _beforeUnloadInstalled = true;
  window.addEventListener("beforeunload", () => {
    // Fire all pending API deletes synchronously via sendBeacon fallback.
    // We can't await, but the setTimeout body was already sync-scheduled.
    _pending.forEach(({ apiCall }) => {
      try {
        apiCall(); // fire-and-forget; browser will hold connection briefly
      } catch (err) {
        // Best-effort — page is unloading, log only in dev
        devDebug("undoDelete beforeunload flush failed", err);
      }
    });
    _pending.clear();
  });
}

/**
 * Optimistic delete dengan tombol Undo di toast.
 *
 * Alur:
 *   1. Item langsung dihilangkan dari state (`setItems`) — UI update instan.
 *   2. Toast muncul dengan tombol "Batalkan" (5 detik).
 *   3. Kalau user klik Batalkan → item dikembalikan ke posisi semula, API tidak dipanggil.
 *   4. Kalau timer habis → API DELETE dipanggil. Kalau gagal → item di-restore + toast error.
 *   5. Kalau user pindah halaman / reload sebelum 5 detik → pending delete
 *      di-flush langsung supaya benar-benar terhapus di server.
 *
 * @param {Object} p
 * @param {Array}  p.items       — state array saat ini
 * @param {Function} p.setItems  — setter React state (menerima array)
 * @param {Object} p.target      — item yang dihapus (harus punya `.id`)
 * @param {Function} p.apiCall   — async () => api.delete(...)
 * @param {string} p.label       — label item untuk toast, mis. "Truk BK1234"
 * @param {Function} [p.onDone]  — callback setelah delete API sukses (mis. reload alerts)
 */
export function scheduleUndoableDelete({ items, setItems, target, apiCall, label, onDone }) {
  _ensureBeforeUnloadHook();

  const idx = items.findIndex((x) => x.id === target.id);
  if (idx === -1) return;
  const backup = items[idx];

  // Optimistic remove
  setItems(items.filter((x) => x.id !== target.id));

  const pendingKey = `${target.id}-${Date.now()}`;
  let cancelled = false;

  const restore = () => {
    setItems((prev) => {
      if (prev.some((x) => x.id === backup.id)) return prev;
      const next = [...prev];
      const insertAt = Math.min(idx, next.length);
      next.splice(insertAt, 0, backup);
      return next;
    });
  };

  const commit = async () => {
    if (cancelled) return;
    _pending.delete(pendingKey);
    try {
      await apiCall();
      if (onDone) onDone();
    } catch (err) {
      restore();
      toast.error(formatApiError(err));
    }
  };

  const timer = setTimeout(commit, 5000);

  _pending.set(pendingKey, {
    timer,
    apiCall, // used by beforeunload flush
    restore,
  });

  toast(`${label} dihapus`, {
    duration: 5000,
    action: {
      label: "Batalkan",
      onClick: () => {
        cancelled = true;
        clearTimeout(timer);
        _pending.delete(pendingKey);
        restore();
        toast.success(`${label} dibatalkan`);
      },
    },
  });
}

/**
 * Force-flush any pending deletes NOW (useful in React unmount effects).
 * Called by usePendingDeleteFlush hook consumers.
 */
export function flushPendingDeletes() {
  _pending.forEach(({ timer, apiCall }, key) => {
    clearTimeout(timer);
    try {
      apiCall();
    } catch (err) {
      devDebug("flushPendingDeletes: apiCall failed", err);
    }
    _pending.delete(key);
  });
}
